/**
 * ScratchCard — CityDooh's second game type (Option B: backend decides
 * the prize at form-submit time, identical timing to Spin Wheel).
 *
 * ┌────────────────────────────────────────────────────────────────┐
 * │  TWO-LAYER CANVAS ARCHITECTURE — DO NOT SIMPLIFY               │
 * ├────────────────────────────────────────────────────────────────┤
 * │  contentCanvas  → the face UNDERNEATH the mask                 │
 * │                    - Before submit: generic "Scratch to reveal"│
 * │                      shimmer face (no prize data).             │
 * │                    - After submit: real prize face             │
 * │                      (prize label + gift_name + branding).     │
 * │                    - Swapping content DOES NOT reset the mask. │
 * │  maskCanvas     → the scratchable overlay ON TOP               │
 * │                    - Pointer/touch drag paints                 │
 * │                      globalCompositeOperation='destination-out'│
 * │                      revealing the layer beneath.              │
 * │                    - Persists across the content swap because  │
 * │                      it lives on a separate canvas.            │
 * └────────────────────────────────────────────────────────────────┘
 *
 *   Threshold table (empirically tuned)
 *   ────────────────────────────────────
 *     PAUSE_AT ≈ 25%  →  freeze input, hand off to LeadFormModal
 *     REVEAL_AT ≈ 75% →  auto-fade the rest, fire onScratchComplete
 *
 * The parent (PlayPage) is IDENTICAL in signature to <RewardWheel> —
 * it passes:
 *     slices, primaryColor, secondaryColor, onSliceText, spinningTo,
 *     onSpinEnd
 * We interpret `spinningTo` (a slice index) as "backend decision has
 * arrived, swap the content layer and unfreeze input". `onSpinEnd`
 * is the callback fired when the mask is fully revealed — same
 * downstream flow (PrizeRevealModal, download win-card, share) with
 * zero forking.
 *
 * A third callback, `onScratchThreshold`, is unique to Scratch Card:
 * fired the moment PAUSE_AT is crossed so the parent knows to open
 * LeadFormModal. If the parent doesn't pass one, we call the parent
 * to submit via its existing handler (see PlayPage `submitForm`).
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { normalizePalette } from "@/lib/safeColors";
import { playScratchLoop, playRevealChime } from "@/lib/scratchSound";

const PAUSE_AT = 0.25;   // 25% cleared → hand off to LeadFormModal
const REVEAL_AT = 0.75;  // 75% cleared → auto-fade remaining mask
const BRUSH_RADIUS = 26; // px in canvas coords
const CANVAS_SIZE = 640; // logical canvas resolution (2× the visible box)
const BOX_SIZE = 320;    // visible pixel size

// Sample the mask every N ms to compute cleared %. 100ms is smooth
// enough for a UI reveal but doesn't melt low-end phones.
const SAMPLE_INTERVAL_MS = 100;
// Sub-sample the mask when computing cleared% — 1 sample per 4 px is
// visually indistinguishable from full-pixel scan on this canvas but
// ~16× faster.
const SAMPLE_STRIDE = 4;

export default function ScratchCard({
  slices = [],
  primaryColor = "#F5A800",
  secondaryColor,
  onSliceText,
  spinningTo,       // int index once backend result is available (contract with <RewardWheel>)
  onSpinEnd,        // called when the mask crosses REVEAL_AT
  onScratchThreshold, // NEW — fired when we cross PAUSE_AT
  scratchSound = "classic",
  campaignName = "Scratch & Win",
  logoUrl,
  testId = "scratch-card",
}) {
  const contentRef = useRef(null);
  const maskRef = useRef(null);
  const wrapRef = useRef(null);
  const pointerDownRef = useRef(false);
  const lastPointRef = useRef(null);
  const clearedRef = useRef(0);
  const contentRevealedRef = useRef(false); // has the "real" prize face been painted?
  const soundCtlRef = useRef(null);
  const autoFadeRafRef = useRef(null);

  const [cleared, setCleared] = useState(0);
  const [phase, setPhase] = useState("teaser"); // teaser | awaiting-submit | revealing | done
  const [reducedMotion, setReducedMotion] = useState(false);

  // Resolve palette safely (same helper Spin Wheel uses)
  const palette = useMemo(
    () => normalizePalette({ primary: primaryColor, secondary: secondaryColor }),
    [primaryColor, secondaryColor]
  );

  // ── prefers-reduced-motion ──────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const listener = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", listener);
    return () => mq.removeEventListener?.("change", listener);
  }, []);

  /* ─── Painting the CONTENT layer ────────────────────────────────
     Two separate paints — one for the mystery teaser (before the
     backend result), one for the real prize. Both draw at CANVAS_SIZE
     resolution regardless of viewport size (we downscale via CSS).
     Called once at mount + again the moment `spinningTo` flips from
     null → int. Never resets the mask.                             */
  const paintTeaser = useCallback(() => {
    const c = contentRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    // Dark base
    const grad = ctx.createLinearGradient(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    grad.addColorStop(0, "#0f172a");
    grad.addColorStop(1, "#1e293b");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    // Gold shimmer stripes (subtle)
    ctx.save();
    ctx.globalAlpha = 0.09;
    ctx.strokeStyle = palette.primary;
    ctx.lineWidth = 2;
    for (let x = -CANVAS_SIZE; x < CANVAS_SIZE * 2; x += 22) {
      ctx.beginPath();
      ctx.moveTo(x, -20);
      ctx.lineTo(x + CANVAS_SIZE, CANVAS_SIZE + 20);
      ctx.stroke();
    }
    ctx.restore();
    // Center prompt
    ctx.fillStyle = palette.primary;
    ctx.font = `900 60px 'Anton', 'Impact', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("MYSTERY", CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 34);
    ctx.fillStyle = "#F5F5F5";
    ctx.font = `700 44px 'Anton', 'Impact', sans-serif`;
    ctx.fillText("PRIZE INSIDE", CANVAS_SIZE / 2, CANVAS_SIZE / 2 + 24);
    ctx.font = `500 22px 'Figtree', Arial, sans-serif`;
    ctx.fillStyle = "rgba(245,245,245,0.75)";
    ctx.fillText("Scratch to reveal", CANVAS_SIZE / 2, CANVAS_SIZE / 2 + 72);
    // Corner decorations
    ctx.font = `600 18px 'Figtree', Arial, sans-serif`;
    ctx.fillStyle = palette.primary;
    ctx.textAlign = "left";
    ctx.fillText("★ CityDooh", 24, 40);
    ctx.textAlign = "right";
    ctx.fillText(campaignName.toUpperCase().slice(0, 22), CANVAS_SIZE - 24, 40);
  }, [palette.primary, campaignName]);

  const paintPrize = useCallback((slice) => {
    const c = contentRef.current;
    if (!c || !slice) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    // Winning card bg
    const grad = ctx.createRadialGradient(
      CANVAS_SIZE / 2, CANVAS_SIZE / 2, 40,
      CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE * 0.7
    );
    grad.addColorStop(0, "#fff8e5");
    grad.addColorStop(1, "#F5A800");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const isBetterLuck = !!slice.is_better_luck;
    if (isBetterLuck) {
      ctx.fillStyle = "#0f172a";
      ctx.font = `900 56px 'Anton', 'Impact', sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("BETTER LUCK", CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 20);
      ctx.fillText("NEXT TIME", CANVAS_SIZE / 2, CANVAS_SIZE / 2 + 46);
    } else {
      // Prize label
      const label = (slice.gift_name || slice.label || "You won!").toString();
      ctx.fillStyle = "#0f172a";
      ctx.font = `600 22px 'Figtree', Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("YOU WON", CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 100);
      ctx.font = `900 62px 'Anton', 'Impact', sans-serif`;
      const wrapped = wrapText(ctx, label, CANVAS_SIZE - 80);
      wrapped.forEach((line, i) => {
        ctx.fillText(
          line,
          CANVAS_SIZE / 2,
          CANVAS_SIZE / 2 - 20 + i * 74
        );
      });
      ctx.font = `500 18px 'Figtree', Arial, sans-serif`;
      ctx.fillStyle = "rgba(15,23,42,0.75)";
      ctx.fillText("Keep scratching to claim", CANVAS_SIZE / 2, CANVAS_SIZE - 40);
    }
  }, []);

  /* ─── Painting the MASK layer ───────────────────────────────────
     Solid opaque cover with a subtle "gold foil" gradient + question
     marks. Scratching uses destination-out to erase pixels; the
     content layer beneath then shows through.                       */
  const paintMask = useCallback(() => {
    const c = maskRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    // Metallic gold-foil vibe
    const grad = ctx.createLinearGradient(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    grad.addColorStop(0, "#b3872a");
    grad.addColorStop(0.5, "#F5A800");
    grad.addColorStop(1, "#8a660a");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    // Diagonal stripes
    ctx.save();
    ctx.globalAlpha = 0.14;
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 3;
    for (let x = -CANVAS_SIZE; x < CANVAS_SIZE * 2; x += 26) {
      ctx.beginPath();
      ctx.moveTo(x, -20);
      ctx.lineTo(x + CANVAS_SIZE, CANVAS_SIZE + 20);
      ctx.stroke();
    }
    ctx.restore();
    // Question marks tiled
    ctx.fillStyle = "rgba(255,255,255,0.20)";
    ctx.font = `900 34px 'Anton', 'Impact', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (let y = 60; y < CANVAS_SIZE; y += 80) {
      for (let x = 60; x < CANVAS_SIZE; x += 80) {
        ctx.fillText("?", x + (y % 160 === 0 ? 0 : 40), y);
      }
    }
    // Big "SCRATCH" label center — visible until first stroke
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = `900 72px 'Anton', 'Impact', sans-serif`;
    ctx.fillText("SCRATCH", CANVAS_SIZE / 2, CANVAS_SIZE / 2);
    ctx.font = `700 28px 'Figtree', Arial, sans-serif`;
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.fillText("Drag your finger", CANVAS_SIZE / 2, CANVAS_SIZE / 2 + 56);
    clearedRef.current = 0;
    setCleared(0);
  }, []);

  // Initial paints
  useEffect(() => {
    paintTeaser();
    paintMask();
  }, [paintTeaser, paintMask]);

  /* ─── React to backend result arriving via `spinningTo` ─────────
     Contract mirrors RewardWheel:
        spinningTo === null  → we're pre-submit (teaser)
        spinningTo === int    → backend responded, swap content       */
  useEffect(() => {
    if (spinningTo === null || spinningTo === undefined) return;
    const winning = slices?.[spinningTo];
    if (!winning) return;
    if (contentRevealedRef.current) return; // idempotent
    contentRevealedRef.current = true;
    paintPrize(winning);
    setPhase("revealing"); // input re-enabled below
  }, [spinningTo, slices, paintPrize]);

  /* ─── Sampling the mask for cleared% ────────────────────────────
     Runs on a throttled interval while the user is actively
     scratching — we don't need to compute per-stroke, per-frame.    */
  useEffect(() => {
    if (phase === "done") return;
    const iv = setInterval(() => {
      const c = maskRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      const w = c.width;
      const h = c.height;
      let opaque = 0;
      let total = 0;
      try {
        const data = ctx.getImageData(0, 0, w, h).data;
        for (let y = 0; y < h; y += SAMPLE_STRIDE) {
          for (let x = 0; x < w; x += SAMPLE_STRIDE) {
            const i = (y * w + x) * 4 + 3; // alpha channel
            total += 1;
            if (data[i] > 20) opaque += 1;
          }
        }
      } catch {
        // Reading pixel data can fail in rare cross-origin edge cases;
        // fall back to the last known value rather than throwing.
        return;
      }
      if (total === 0) return;
      const clearedPct = 1 - opaque / total;
      clearedRef.current = clearedPct;
      setCleared(clearedPct);
    }, SAMPLE_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [phase]);

  /* ─── Crossing thresholds ───────────────────────────────────────
     PAUSE_AT: freeze input, ask parent to open LeadFormModal.
     REVEAL_AT: fade remaining mask, fire onSpinEnd (same contract
                as RewardWheel).                                      */
  useEffect(() => {
    if (phase === "teaser" && cleared >= PAUSE_AT) {
      setPhase("awaiting-submit");
      stopSound();
      onScratchThreshold?.();
    }
    if (phase === "revealing" && cleared >= REVEAL_AT) {
      setPhase("done");
      stopSound();
      autoFadeMask(() => {
        try { playRevealChime(scratchSound); } catch { /* silent */ }
        onSpinEnd?.();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleared, phase]);

  /* ─── Input handling ────────────────────────────────────────────
     Locked whenever we're outside `teaser` or `revealing`. Uses
     pointer events for unified mouse+touch handling; `.setPointerCapture`
     to keep tracking even if the user drags outside the canvas.     */
  const inputAllowed = phase === "teaser" || phase === "revealing";

  const scratch = useCallback((clientX, clientY) => {
    const c = maskRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const scale = CANVAS_SIZE / rect.width;
    const x = (clientX - rect.left) * scale;
    const y = (clientY - rect.top) * scale;
    const ctx = c.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    // Connect to previous point for smooth strokes even when the pointer
    // moves faster than the sample rate.
    const prev = lastPointRef.current;
    if (prev) {
      ctx.lineWidth = BRUSH_RADIUS * 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      ctx.arc(x, y, BRUSH_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
    lastPointRef.current = { x, y };
    if (!reducedMotion) {
      spawnParticles(wrapRef.current, clientX, clientY, palette.primary);
    }
  }, [palette.primary, reducedMotion]);

  const onPointerDown = (e) => {
    if (!inputAllowed) return;
    pointerDownRef.current = true;
    lastPointRef.current = null;
    try { e.target.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
    startSound();
    scratch(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (!pointerDownRef.current || !inputAllowed) return;
    scratch(e.clientX, e.clientY);
  };
  const onPointerUp = () => {
    pointerDownRef.current = false;
    lastPointRef.current = null;
    stopSound();
  };

  /* ─── Sound loop ────────────────────────────────────────────────
     Delegated to /lib/scratchSound so PlayPage-side wiring is
     identical to spin sounds. Silent when scratchSound === "none". */
  const startSound = () => {
    if (soundCtlRef.current) return;
    try {
      soundCtlRef.current = playScratchLoop(scratchSound);
    } catch { /* audio failures are non-fatal */ }
  };
  const stopSound = () => {
    try { soundCtlRef.current?.stop?.(); } catch { /* noop */ }
    soundCtlRef.current = null;
  };
  useEffect(() => () => stopSound(), []);

  /* ─── Auto-fade the last of the mask ────────────────────────────
     Runs at 60fps for ~500ms — reducing global alpha of the mask
     to zero. Fires the onDone callback afterwards.                  */
  const autoFadeMask = (done) => {
    const c = maskRef.current;
    if (!c) { done?.(); return; }
    const ctx = c.getContext("2d");
    const start = performance.now();
    const DURATION = reducedMotion ? 200 : 500;
    const step = (t) => {
      const elapsed = t - start;
      const progress = Math.min(elapsed / DURATION, 1);
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = `rgba(0,0,0,${0.15 + progress * 0.15})`;
      ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      if (progress < 1) {
        autoFadeRafRef.current = requestAnimationFrame(step);
      } else {
        ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        done?.();
      }
    };
    autoFadeRafRef.current = requestAnimationFrame(step);
  };
  useEffect(() => () => {
    if (autoFadeRafRef.current) cancelAnimationFrame(autoFadeRafRef.current);
  }, []);

  /* ─── Reduced-motion fallback ───────────────────────────────────
     Two-tap flow: tap once to reveal the mystery card + fire the
     threshold callback, tap again after backend result to reveal
     the prize.                                                       */
  const handleReducedMotionTap = () => {
    if (phase === "teaser") {
      // Simulate 40% cleared instantly.
      const c = maskRef.current;
      if (c) {
        const ctx = c.getContext("2d");
        ctx.globalCompositeOperation = "destination-out";
        ctx.fillStyle = "rgba(0,0,0,1)";
        ctx.fillRect(60, 60, CANVAS_SIZE - 120, CANVAS_SIZE - 120);
      }
      clearedRef.current = 0.4;
      setCleared(0.4);
    } else if (phase === "revealing") {
      // Clear everything at once.
      autoFadeMask(() => {
        try { playRevealChime(scratchSound); } catch { /* silent */ }
        onSpinEnd?.();
      });
    }
  };

  /* ─── Render ────────────────────────────────────────────────────
     Wrapper is `relative` so we can absolutely-position particles
     children (spawned via spawnParticles). Both canvases share the
     same box and same logical resolution; CSS scales them.          */
  return (
    <div
      ref={wrapRef}
      className="relative select-none"
      style={{
        width: BOX_SIZE,
        height: BOX_SIZE,
        touchAction: "none",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
      data-testid={testId}
      data-scratch-phase={phase}
      data-scratch-progress={cleared.toFixed(2)}
    >
      {/*
        Note: the vendor logo used to be rendered here as a small floating
        badge above the card. Removed because PlayPage already shows the
        vendor logo prominently in the page header — the second render
        below it created visual redundancy (visible in mobile viewport)
        and duplicated the brand for no benefit. The `logoUrl` prop is
        kept in the component contract so the shared Spin/Scratch API
        remains uniform, but we no longer paint it here.
      */}
      <canvas
        ref={contentRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        className="absolute inset-0 rounded-2xl"
        style={{
          width: BOX_SIZE, height: BOX_SIZE,
          boxShadow: "6px 6px 0 rgba(15,23,42,0.35)",
        }}
        aria-hidden="true"
      />
      <canvas
        ref={maskRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        className="absolute inset-0 rounded-2xl"
        style={{
          width: BOX_SIZE, height: BOX_SIZE,
          cursor: inputAllowed ? (reducedMotion ? "pointer" : "grab") : "not-allowed",
          opacity: phase === "done" ? 0 : 1,
          transition: "opacity 200ms ease-out",
        }}
        onPointerDown={reducedMotion ? undefined : onPointerDown}
        onPointerMove={reducedMotion ? undefined : onPointerMove}
        onPointerUp={reducedMotion ? undefined : onPointerUp}
        onPointerLeave={reducedMotion ? undefined : onPointerUp}
        onClick={reducedMotion ? handleReducedMotionTap : undefined}
        data-testid={`${testId}-mask`}
        role="button"
        aria-label={
          phase === "teaser"      ? "Scratch to enter the prize draw"
          : phase === "revealing" ? "Scratch to reveal your prize"
          : "Prize revealed"
        }
      />
      {/* Screen-reader progress cue */}
      <span className="sr-only" role="status">
        {phase === "teaser" && `Card ${Math.round(cleared * 100)}% scratched.`}
        {phase === "awaiting-submit" && "Please fill in your details to continue."}
        {phase === "revealing" && `Prize being revealed, ${Math.round(cleared * 100)}% cleared.`}
        {phase === "done" && "Prize fully revealed."}
      </span>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────── */

function wrapText(ctx, text, maxWidth) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, 2); // hard-cap 2 lines
}

/**
 * Spawn 4-8 short-lived DOM particles at the pointer location. Pure
 * CSS animations — no external asset. Auto-cleanup after 700ms.
 */
function spawnParticles(container, clientX, clientY, color) {
  if (!container) return;
  const rect = container.getBoundingClientRect();
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  const n = 3 + Math.floor(Math.random() * 3);
  for (let i = 0; i < n; i++) {
    const el = document.createElement("span");
    el.style.position = "absolute";
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.width = "6px";
    el.style.height = "6px";
    el.style.borderRadius = "50%";
    el.style.background = color;
    el.style.pointerEvents = "none";
    el.style.transform = "translate(-50%, -50%)";
    el.style.zIndex = "50";
    const dx = (Math.random() - 0.5) * 60;
    const dy = -20 - Math.random() * 40;
    el.animate(
      [
        { transform: `translate(-50%, -50%) translate(0,0) scale(1)`, opacity: 1 },
        { transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(0.2)`, opacity: 0 },
      ],
      { duration: 650, easing: "cubic-bezier(.3,.7,.2,1)" }
    );
    container.appendChild(el);
    setTimeout(() => el.remove(), 700);
  }
}
